import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import globalSettingsBranches from '../../../../../stores/global-settings-branches';
import globalSettingsStores from '../../../../../stores/settings-stores';
import { getHebrewYear } from '../../../../../utils/hebrew-date-utils';
import { Button } from 'primereact/button';
import { FilterExpanderWithListOfCheckboxes } from '../../../../../components/CustomExpander/componenets/filter-expander-with-list-of-checkboxes';
import { StudentCourseByStatusFilters } from './student-course-by-status-side-bar-filter.type';
import {
  ProgramStudentStatus,
  getProgramStudentStatusDisplayText,
} from '../../../../../api/types/programs/students/program-student-status';

interface StudentCourseByStatusSideBarFilterProps {
  onSearch: (filters: StudentCourseByStatusFilters) => void;
}

interface YearOption {
  year: string;
  code: string;
  value: number;
}

export const StudentCourseByStatusSideBarFilter = observer(
  ({ onSearch }: StudentCourseByStatusSideBarFilterProps) => {
    const [selectedBranchIds, setSelectedBranchIds] = useState<number[]>([]);
    const [selectedProgramIds, setSelectedProgramIds] = useState<number[]>([]);
    const [selectedYears, setSelectedYears] = useState<number[]>([]);
    const [selectedCourseStatuses, setSelectedCourseStatuses] = useState<
      number[]
    >([]);
    const [selectedProgramStatuses, setSelectedProgramStatuses] = useState<
      ProgramStudentStatus[]
    >([]);

    // Generate years array
    const years: YearOption[] = [];
    for (let year = 2000; year <= 2028; year++) {
      years.push({
        year: `${getHebrewYear(year)} (${year} - ${year + 1})`,
        code: 'year',
        value: year,
      });
    }

    const handleSearch = () => {
      const filters: StudentCourseByStatusFilters = {
        branchIds: selectedBranchIds,
        abstractProgramIds: selectedProgramIds,
        startYears: selectedYears,
        studentStatusInCourse: selectedCourseStatuses,
        studentStatusInProgram: selectedProgramStatuses,
      };
      onSearch(filters);
    };

    const styles = {
      container: {
        height: '100%',
        background: 'white',
        borderRadius: '8px',
        padding: '16px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        display: 'flex',
        flexDirection: 'column' as const,
      },
      header: {
        fontSize: '1.2rem',
        fontWeight: 'bold',
        marginBottom: '16px',
        color: '#333',
      },
      content: {
        flex: 1,
        overflowY: 'auto' as const,
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '8px',
        paddingRight: '4px',
      },
      buttonContainer: {
        marginTop: '16px',
        padding: '8px',
        borderTop: '1px solid #e9ecef',
      },
      searchButton: {
        width: '100%',
        backgroundColor: '#2196F3',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        padding: '12px',
        fontSize: '1rem',
        fontWeight: 'bold',
        cursor: 'pointer',
        transition: 'background-color 0.2s',
      },
    };

    return (
      <div style={styles.container}>
        <div style={styles.header}>סינונים</div>
        <div style={styles.content}>
          <FilterExpanderWithListOfCheckboxes
            title="שלוחות"
            items={globalSettingsBranches.branches}
            selectedItems={selectedBranchIds}
            onItemToggle={(branchId) => {
              setSelectedBranchIds((prev) =>
                prev.includes(branchId)
                  ? prev.filter((b) => b !== branchId)
                  : [...prev, branchId],
              );
            }}
            getItemLabel={(branch) => branch.branchName}
            getItemKey={(branch) => branch.id}
            defaultExpanded={true}
            searchPlaceholder="חפש שלוחה..."
          />

          <FilterExpanderWithListOfCheckboxes
            title="תוכניות לימודים"
            items={globalSettingsStores.abstractProgram}
            selectedItems={selectedProgramIds}
            onItemToggle={(programId) => {
              setSelectedProgramIds((prev) =>
                prev.includes(programId)
                  ? prev.filter((p) => p !== programId)
                  : [...prev, programId],
              );
            }}
            getItemLabel={(program) => program.programName}
            getItemKey={(program) => program.id}
            searchPlaceholder="חפש תכנית..."
          />

          <FilterExpanderWithListOfCheckboxes
            title="שנת התחלה"
            items={years}
            selectedItems={selectedYears.map(String)}
            onItemToggle={(yearStr) => {
              const year = parseInt(yearStr);
              setSelectedYears((prev) =>
                prev.includes(year)
                  ? prev.filter((y) => y !== year)
                  : [...prev, year],
              );
            }}
            getItemLabel={(yearOption) => yearOption.year}
            getItemKey={(yearOption) => yearOption.value.toString()}
            searchPlaceholder="חפש שנה..."
          />

          <FilterExpanderWithListOfCheckboxes
            title="סטטוס סטודנט בקורס"
            items={globalSettingsStores.courseStudentStatuses}
            selectedItems={selectedCourseStatuses}
            onItemToggle={(statusId) => {
              setSelectedCourseStatuses((prev) =>
                prev.includes(statusId)
                  ? prev.filter((s) => s !== statusId)
                  : [...prev, statusId],
              );
            }}
            getItemLabel={(status) => status.statusName}
            getItemKey={(status) => status.id}
            searchPlaceholder="חפש סטטוס..."
          />

          <FilterExpanderWithListOfCheckboxes
            title="סטטוס סטודנט בתוכנית לימודים"
            items={Object.values(ProgramStudentStatus)}
            selectedItems={selectedProgramStatuses}
            onItemToggle={(status: ProgramStudentStatus) => {
              setSelectedProgramStatuses((prev) =>
                prev.includes(status)
                  ? prev.filter((s) => s !== status)
                  : [...prev, status],
              );
            }}
            getItemLabel={(status) =>
              getProgramStudentStatusDisplayText(status)
            }
            getItemKey={(status) => status}
            searchPlaceholder="חפש סטטוס..."
          />
        </div>
        <div style={styles.buttonContainer}>
          <Button
            label="חפש"
            icon="pi pi-search"
            style={styles.searchButton}
            onClick={handleSearch}
          />
        </div>
      </div>
    );
  },
);
